import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, HeartHandshake, ClipboardCheck, Users, Award, Target, Eye, Calendar, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
const VALUES = [{
  letter: 'I',
  title: 'Integrity',
  description: "We adhere to the highest ethical principles.",
  Icon: ShieldCheck
}, {
  letter: 'C',
  title: 'Commitment',
  description: "We are devoted in what we do.",
  Icon: HeartHandshake
}, {
  letter: 'A',
  title: 'Accountability',
  description: "We are accountable to one another and to our community.",
  Icon: ClipboardCheck
}, {
  letter: 'R',
  title: 'Respect',
  description: "We welcome everyone, creating a culture of inclusion, empathy and belonging.",
  Icon: Users
}, {
  letter: 'E',
  title: 'Excellence',
  description: "We are striving to be the best that we can be, innovating and learning from others.",
  Icon: Award
}];
export function About() {
  return <div className="w-full bg-white" data-id="element-392" data-mp="1lrydlrdoi1sx">
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-navy" data-id="element-393" data-mp="1uitfn6m2pdt2">
        <img src="https://cdn.magicpatterns.com/uploads/bPHihZ6KTfWZdQRzutubCy/maisha_edited_6.jpg" alt="Maisha Medical Center reception and waiting area" className="absolute inset-0 w-full h-full object-cover" data-id="element-394" data-mp="1piyqp8luyyan" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/70 to-brand-navy/30" data-id="element-395" data-mp="1yj3y3ir1xwot" />
        <div className="relative z-10 max-w-brand mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28" data-id="element-396" data-mp="2dfmcfcug8ws5">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="max-w-2xl text-white" data-id="element-397" data-mp="28gi3mnbft12c">
            <span className="font-semibold tracking-wider text-sm uppercase mb-3 block text-[#FFFFFF]" data-id="element-398" data-mp="2i85zr1ixv6a5">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-6 text-[#FFFFFF]" data-id="element-399" data-mp="2r8avufiomi90">
              Trusted, Patient-Centered Care Since 2013
            </h1>
            <p className="text-slate-200 text-lg leading-relaxed" data-id="element-400" data-mp="1svykeeo7ky16">
              Maisha Medical Center is committed to providing accessible,
              high-quality healthcare to individuals and families across
              Botswana.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 overflow-hidden" data-id="element-401" data-mp="27lxeo6cn7pbd">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-402" data-mp="27n8lyg5arf78">
          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-403" data-mp="2ciw62h4x9i5v">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="w-full lg:w-1/2 relative" data-id="element-404" data-mp="2glkh8vh3z160">
              <div className="grid grid-cols-2 gap-4" data-id="element-405" data-mp="r62xbr1qipos">
                <img src="https://cdn.magicpatterns.com/uploads/n6zAT8tgvM5ciksZ9UXwaH/20231219005952_IMG_0691-01.jpg" alt="Maisha clinician performing an ear examination on a young patient" className="rounded-2xl shadow-soft object-cover h-72 w-full mt-8" data-id="element-406" data-mp="r8pfekm94nl9" />
                <img src="https://cdn.magicpatterns.com/uploads/abv6TkKQVb179pmoWXHRQg/maisha_edited.jpg" alt="Maisha physiotherapist assessing a patient in a wheelchair" className="rounded-2xl shadow-soft object-cover h-72 w-full" data-id="element-407" data-mp="9ybr1ro2l41d" />
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="w-full lg:w-1/2" data-id="element-408" data-mp="xmuyy637jesx">
              <span className="text-brand-red font-semibold tracking-wider text-sm uppercase mb-3 block" data-id="element-409" data-mp="3des90oi63k00">
                Our Story
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-navy mb-6 leading-tight" data-id="element-410" data-mp="39bdts021z7nm">
                Dedicated to the Health of Our Community
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed" data-id="element-411" data-mp="15wa82cosdo8l">
                NORA-PACK TECHNICS (PTY) LTD operates Maisha Medical Center,
                registered in Botswana since 2013.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed" data-id="element-412" data-mp="w2tdv7kqm74e">
                Managed by{' '}
                <strong className="text-brand-navy font-semibold" data-id="element-413" data-mp="1jsdv9m18swbk">
                  Dr. Gaston Nduenga Zanzambi
                </strong>
                , the center is committed to providing accessible, high-quality,
                and patient-centered healthcare for individuals and families
                across the region.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-brand-lighter/40" data-id="element-414" data-mp="3ggyzxwh2ybi">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-415" data-mp="3dvv73cm68b4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-416" data-mp="3s0smpzavfaal">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4
          }} className="bg-brand-red rounded-2xl p-8 md:p-10 text-white shadow-soft-lg" data-id="element-417" data-mp="3n3imdoly1e7z">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-6" data-id="element-418" data-mp="39c3zv7stuuw6">
                <Target className="w-6 h-6" data-id="element-419" data-mp="34ecb2mnhbow7" />
              </div>
              <h3 className="font-heading font-bold text-2xl mb-4" data-id="element-420" data-mp="3j1nj4ekgcqrk">
                Our Mission
              </h3>
              <p className="text-white/90 text-lg leading-relaxed" data-id="element-421" data-mp="3e9x7845v9uy1">
                To promote health and well-being by delivering professional,
                compassionate, and evidence-based medical care.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.1
          }} className="bg-brand-navy rounded-2xl p-8 md:p-10 text-white shadow-soft-lg" data-id="element-422" data-mp="3ba5cy19ikhkj">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-6" data-id="element-423" data-mp="3p121nh0bkemb">
                <Eye className="w-6 h-6 text-brand-gold" data-id="element-424" data-mp="2sd47kfc1sfg2" />
              </div>
              <h3 className="font-heading font-bold text-2xl mb-4 text-[#FFFFFF]" data-id="element-425" data-mp="2wdo2rdd298zk">
                Our Vision
              </h3>
              <p className="text-slate-200 text-lg leading-relaxed" data-id="element-426" data-mp="31e2cr0y82t08">
                To establish a first-class, patient-focused service that
                delivers excellence in a welcoming environment.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values — I.C.A.R.E */}
      <section className="py-24" data-id="element-427" data-mp="262w1a8c2jgn9">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-428" data-mp="261uhnfgzlltx">
          <div className="text-center max-w-2xl mx-auto mb-14" data-id="element-429" data-mp="2142pqlgcdzpq">
            <span className="text-brand-red font-semibold tracking-wider text-sm uppercase mb-3 block" data-id="element-430" data-mp="214kr1whv344i">
              What We Stand For
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-navy mb-4" data-id="element-431" data-mp="1rerhf11a4z2w">
              Our Values — I.C.A.R.E
            </h2>
            <p className="text-slate-600 text-lg" data-id="element-432" data-mp="1nabftbyp8dth">
              Everyday we deliver safe and high quality healthcare driven by our I.C.A.R.E. values
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-433" data-mp="1deqg5kh3rpmw">
            {VALUES.map(({
            letter,
            title,
            description,
            Icon
          }, idx) => <motion.div key={title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: idx * 0.08
          }} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-shadow text-center" data-id="element-434" data-mp="37rpu795u4bs2" data-mpi={idx}>
                <div className="w-14 h-14 rounded-2xl bg-brand-lighter flex items-center justify-center mx-auto mb-5" data-id="element-435" data-mp="22tnzyudozruq">
                  <Icon className="w-7 h-7 text-brand-red" data-id="element-436" data-mp="1swrs8qvmb9r8" />
                </div>
                <div className="text-3xl font-heading font-bold text-brand-gold mb-1" data-id="element-437" data-mp="1xwe4awzpmjyg">
                  {letter}
                </div>
                <h3 className="font-heading font-bold text-lg text-brand-navy mb-2" data-id="element-438" data-mp="1jxo8dkommqbo">
                  {title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-439" data-mp="1nyhxghdt2b65">
                  {description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24" data-id="element-440" data-mp="3ia4ywmr7d9vi">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-441" data-mp="16odz3c0o4yry">
          <div className="relative overflow-hidden rounded-3xl bg-brand-navy px-8 py-14 md:px-16 md:py-16 text-center" data-id="element-442" data-mp="16mjczudnv0ma">
            <div className="relative z-10 max-w-2xl mx-auto" data-id="element-443" data-mp="1gefcn9veaysm">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4" data-id="element-444" data-mp="1gg07l0lmgjr0">
                Your health is our devotion
              </h2>
              <p className="text-slate-200 text-lg mb-8" data-id="element-445" data-mp="1qc3k90e3o0s7">
                Book an appointment today and experience patient-centered care
                at Maisha Medical Center.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-446" data-mp="1ze2yjkxqcqrg">
                <Button href="/book" variant="primary" size="lg" icon={<Calendar className="w-5 h-5" data-id="element-448" data-mp="1uezua2pkqokj" />} data-id="element-447" data-mp="1ueq3we0mafeb">
                  Book Appointment
                </Button>
                <a href="https://wa.me/26773241613" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] text-white text-lg font-medium px-8 py-4 hover:bg-[#20bd5a] transition-colors shadow-md hover:shadow-lg" data-id="element-449" data-mp="4zscfk8isapd">
                  <MessageCircle className="w-5 h-5" data-id="element-450" data-mp="37dd0empzxn30" />
                  Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}